import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  IDoesFilterPassParams,
  IFilterComp,
  IFilterParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import { AgFilterComponent } from 'ag-grid-angular';

export interface SingleSelectFilterParams extends IFilterParams {
  values: string[] | ((params: IFilterParams) => string[] | Promise<string[]>);
  defaultValue?: string;
  suppressSort?: boolean;
  placeholder?: string;
}

@Component({
    selector: 'app-ag-grid-single-select-filter',
    template: `
    <div class="ag-filter-body-wrapper">
      <select 
        #selectEl
        [(ngModel)]="selectedValue"
        (ngModelChange)="onChange()"
        class="ag-filter-select tw-font-sans tw-text-sm tw-w-full tw-px-2 tw-py-1.5 tw-my-1.5 tw-bg-white tw-text-gray-900 tw-border tw-border-gray-300 tw-rounded focus:tw-ring-1 focus:tw-ring-blue-500 focus:tw-border-blue-500 dark:tw-bg-gray-800 dark:tw-text-gray-100 dark:tw-border-gray-600 dark:focus:tw:ring-blue-400 dark:focus:tw:border-blue-400">
        <option value="" class="tw-text-gray-900 dark:tw-text-gray-100">{{ placeholder }}</option>
        <option *ngFor="let value of values" [value]="value" class="tw-text-gray-900 dark:tw-text-gray-100">
          {{ value }}
        </option>
      </select>
    </div>
  `,
    standalone: false
})
export class AGGridSingleSelectFilterComponent implements AgFilterComponent {
  private params!: SingleSelectFilterParams;
  public selectedValue: string = '';
  public values: string[] = [];
  public placeholder: string = '(All)';
  private hidePopup?: () => void;
  @ViewChild('selectEl') private selectRef?: ElementRef<HTMLSelectElement>;
  private attemptedAutoOpen = false;

  agInit(params: SingleSelectFilterParams): void {
    this.params = params;
    this.placeholder = params.placeholder || '(All)';

    // Handle values - check if it's a function or array
    if (typeof params.values === 'function') {
      const result = params.values(params);
      if (result instanceof Promise) {
        result.then((values) => {
          this.setValues(values);
        });
      } else {
        this.setValues(result);
      }
    } else {
      this.setValues(params.values);
    }

    // Set default value if provided
    if (params.defaultValue) {
      this.selectedValue = params.defaultValue;
      // Ensure the default selection is applied immediately
      this.params.filterChangedCallback();
    }
  }

  private setValues(values: string[]): void {
    this.values = values;

    // Sort values if not suppressed
    if (!this.params.suppressSort) {
      this.values.sort();
    }

    // If values arrive asynchronously, try opening the select once
    this.tryOpenSelectDropdown();
  }

  onChange(): void {
    this.params.filterChangedCallback();
    // Close the filter popup/menu if present
    if (this.hidePopup) {
      this.hidePopup();
    } else {
      // Fallback for older contexts
      this.params.api.hidePopupMenu();
    }
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (!this.selectedValue || this.selectedValue === '') {
      return true;
    }

    // Handle null or undefined values
    if (params.data == null) {
      return false;
    }

    // Convert to string for comparison
    return String(params.data[this.params.colDef.field!]) === this.selectedValue;
  }

  isFilterActive(): boolean {
    return this.selectedValue !== null && this.selectedValue !== '';
  }

  getModel(): string | null {
    return this.isFilterActive() ? this.selectedValue : null;
  }

  // Provide a string representation so AG Grid can display it in the floating filter/header
  getModelAsString(model?: any): string {
    const value = model !== undefined ? model : this.getModel();
    return value ?? '';
  }

  setModel(model: string | null): void {
    this.selectedValue = model || '';
  }

  afterGuiAttached(params?: IAfterGuiAttachedParams): void {
    this.hidePopup = params?.hidePopup ?? this.hidePopup;
    // Reset auto-open guard on each popup attach so it opens every time
    this.attemptedAutoOpen = false;
    // Attempt to open the select dropdown when the popup attaches
    this.tryOpenSelectDropdown();
  }

  // Optional: Update values dynamically
  public updateValues(newValues: string[]): void {
    const currentSelection = this.selectedValue;
    this.setValues(newValues);

    // Restore selection if it still exists in new values
    if (currentSelection && this.values.includes(currentSelection)) {
      this.selectedValue = currentSelection;
    } else {
      this.selectedValue = '';
    }
  }

  private tryOpenSelectDropdown(): void {
    if (this.attemptedAutoOpen) {
      return;
    }
    const el = this.selectRef?.nativeElement;
    if (!el) {
      return;
    }
    this.attemptedAutoOpen = true;
    // Defer to ensure the element is painted in the popup
    requestAnimationFrame(() => {
      try {
        const anyEl = el as any;
        if (typeof anyEl.showPicker === 'function') {
          anyEl.showPicker();
          return;
        }
      } catch {
        // ignore
      }
      // Fallbacks that are best-effort
      try {
        el.focus({ preventScroll: true });
        // Some browsers open the native select on click/space when focused
        el.click();
      } catch {
        // ignore
      }
    });
  }

  destroy(): void {
    // Cleanup handled by Angular
  }
}
